
import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CapTable } from "./CapTable";
import { FundingTracker } from "./FundingTracker";
import { InvestorMetrics } from "./InvestorMetrics";
import { NZTaxManager } from "./NZTaxManager";
import { Badge } from "@/components/ui/badge";
import { Calculator, DollarSign, TrendingUp, FileBarChart } from "lucide-react";
import { 
  CapTableEntry, 
  FundingRound, 
  InvestorKPI, 
  NZTaxTreatment,
  TaxLiabilityItem,
  TaxBenefitItem
} from "@/lib/types";
import { calculateRunway } from "@/lib/capital-calculations";

// Sample data - in a real app would come from a database
const initialCapTable: CapTableEntry[] = [
  {
    id: "1",
    shareholderName: "Founder A",
    shareholderType: "founder",
    shareClass: "ordinary",
    sharesIssued: 1000000,
    issuePrice: 0.001,
    issueDate: "2023-01-15",
    dilutedOwnership: 60,
    value: 600000
  },
  {
    id: "2",
    shareholderName: "Founder B",
    shareholderType: "founder",
    shareClass: "ordinary",
    sharesIssued: 500000,
    issuePrice: 0.001,
    issueDate: "2023-01-15",
    dilutedOwnership: 30,
    value: 300000
  },
  {
    id: "3",
    shareholderName: "Angel Investor",
    shareholderType: "investor",
    shareClass: "preference",
    sharesIssued: 166667,
    issuePrice: 0.60,
    issueDate: "2023-06-01",
    dilutedOwnership: 10,
    value: 100000
  }
];

const initialFundingRounds: FundingRound[] = [
  {
    id: "1",
    name: "Pre-seed",
    date: "2023-06-01",
    preMoneyValuation: 900000,
    postMoneyValuation: 1000000,
    amountRaised: 100000,
    leadInvestor: "Angel Investor Group",
    newSharesIssued: 166667,
    sharePrice: 0.60,
    dilution: 10,
    notes: "First external investment"
  }
];

const initialKPIs: InvestorKPI[] = [
  {
    id: "1",
    name: "Cash Runway",
    category: "financial",
    value: 12,
    unit: "months",
    target: 18,
    previousValue: 8,
    changePercentage: 50
  },
  {
    id: "2",
    name: "Burn Rate",
    category: "financial",
    value: 25000,
    unit: "NZD/month",
    target: 20000,
    previousValue: 30000,
    changePercentage: -16.7
  },
  {
    id: "3",
    name: "Mineral Resource Value",
    category: "valuation",
    value: 3500000,
    unit: "NZD",
    target: 5000000,
    previousValue: 2800000,
    changePercentage: 25
  },
  {
    id: "4",
    name: "NPV (8% discount)",
    category: "valuation",
    value: 2100000,
    unit: "NZD",
    target: 3000000,
    previousValue: 1800000,
    changePercentage: 16.7
  }
];

const initialTaxTreatments: NZTaxTreatment[] = [
  {
    id: "1",
    category: "exploration",
    expenseType: "Prospecting",
    deductible: true,
    depreciable: false,
    applicableLegislation: "Income Tax Act 2007, Section DU 1",
    notes: "Fully deductible in the year incurred"
  },
  {
    id: "2",
    category: "exploration",
    expenseType: "Exploration",
    deductible: true,
    depreciable: false,
    applicableLegislation: "Income Tax Act 2007, Section DU 1",
    notes: "Fully deductible in the year incurred"
  },
  {
    id: "3",
    category: "development",
    expenseType: "Mine development",
    deductible: false,
    depreciable: true,
    depreciationRate: 0.1,
    applicableLegislation: "Income Tax Act 2007, Section DU 2",
    notes: "Capitalized and depreciated over the life of the mine or at 10% straight line"
  }
];

const initialTaxLiabilities: TaxLiabilityItem[] = [
  {
    id: "1",
    year: 2025,
    taxableIncome: 0,
    taxRate: 28,
    taxPayable: 0,
    taxCredits: 0,
    netTaxLiability: 0,
    notes: "Exploration phase, no taxable income"
  },
  {
    id: "2",
    year: 2026,
    taxableIncome: 150000,
    taxRate: 28,
    taxPayable: 42000,
    taxCredits: 10000,
    netTaxLiability: 32000,
    notes: "Start of production phase"
  }
];

const initialTaxBenefits: TaxBenefitItem[] = [
  {
    id: "1",
    name: "Research & Development Tax Incentive",
    type: "credit",
    value: 0.15,
    applicableYears: [2025, 2026],
    legislationReference: "Income Tax Act 2007, Section LY",
    requirements: "Eligible R&D activities related to mining technology or processes",
    notes: "15% tax credit on eligible R&D expenditure"
  },
  {
    id: "2",
    name: "Exploration expenditure deduction",
    type: "deduction",
    value: 1.0,
    applicableYears: [2025],
    legislationReference: "Income Tax Act 2007, Section DU 1",
    requirements: "Eligible exploration activities",
    notes: "100% deduction for qualifying exploration expenditure"
  }
];

export function CapitalManagement() {
  // State for capital management data
  const [capTable, setCapTable] = useState<CapTableEntry[]>([]);
  const [fundingRounds, setFundingRounds] = useState<FundingRound[]>([]);
  const [kpis, setKpis] = useState<InvestorKPI[]>([]);
  const [taxTreatments, setTaxTreatments] = useState<NZTaxTreatment[]>([]);
  const [taxLiabilities, setTaxLiabilities] = useState<TaxLiabilityItem[]>([]);
  const [taxBenefits, setTaxBenefits] = useState<TaxBenefitItem[]>([]);
  
  // Calculate capital management summary metrics
  const totalShares = capTable.reduce((sum, entry) => sum + entry.sharesIssued, 0);
  const latestValuation = fundingRounds.length > 0 
    ? fundingRounds[fundingRounds.length - 1].postMoneyValuation 
    : 0;
  const totalRaised = fundingRounds.reduce((sum, round) => sum + round.amountRaised, 0);
  const currentCashBalance = 0; // Set to 0 until real data is added
  const monthlyBurn = 0; // Set to 0 until real data is added
  const runway = currentCashBalance > 0 && Math.abs(monthlyBurn) > 0 
    ? calculateRunway(currentCashBalance, Math.abs(monthlyBurn))
    : 0;
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Capital Management</h1>
        <p className="text-muted-foreground">
          Track equity ownership, funding, investor metrics, and NZ tax information
        </p>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>Company Valuation</span>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(latestValuation / 1000000).toFixed(2)}M</div>
            <p className="text-xs text-muted-foreground mt-1">
              Last funding: {fundingRounds.length > 0 ? fundingRounds[fundingRounds.length - 1].date : "N/A"}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>Total Funding</span>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(totalRaised / 1000).toFixed(0)}K</div>
            <p className="text-xs text-muted-foreground mt-1">
              {fundingRounds.length} funding round{fundingRounds.length !== 1 ? "s" : ""}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>Cash Balance</span>
              <Calculator className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(currentCashBalance / 1000).toFixed(0)}K</div>
            <p className="text-xs text-muted-foreground mt-1">
              Burn: ${Math.abs(monthlyBurn / 1000).toFixed(0)}K/mo
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span>Cash Runway</span>
              <FileBarChart className="h-4 w-4 text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{runway.toFixed(1)} months</div>
            <p className="text-xs text-muted-foreground mt-1">
              {runway <= 6 ? 
                <Badge variant="destructive">Critical</Badge> : 
                runway <= 12 ? 
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Caution</Badge> : 
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Healthy</Badge>
              }
            </p>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs for different sections */}
      <Tabs defaultValue="captable" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full">
          <TabsTrigger value="captable">Cap Table</TabsTrigger>
          <TabsTrigger value="funding">Funding Tracker</TabsTrigger>
          <TabsTrigger value="metrics">Investor Metrics</TabsTrigger>
          <TabsTrigger value="tax">NZ Tax Manager</TabsTrigger>
        </TabsList>
        
        <TabsContent value="captable" className="mt-4">
          <CapTable capTable={capTable} setCapTable={setCapTable} />
        </TabsContent>
        
        <TabsContent value="funding" className="mt-4">
          <FundingTracker 
            fundingRounds={fundingRounds} 
            setFundingRounds={setFundingRounds} 
            capTable={capTable}
            setCapTable={setCapTable}
          />
        </TabsContent>
        
        <TabsContent value="metrics" className="mt-4">
          <InvestorMetrics 
            kpis={kpis} 
            setKpis={setKpis}
            latestValuation={latestValuation}
            totalRaised={totalRaised}
            currentCashBalance={currentCashBalance}
            monthlyBurn={monthlyBurn}
            runway={runway}
          />
        </TabsContent>
        
        <TabsContent value="tax" className="mt-4">
          <NZTaxManager 
            taxTreatments={taxTreatments}
            setTaxTreatments={setTaxTreatments}
            taxLiabilities={taxLiabilities}
            setTaxLiabilities={setTaxLiabilities}
            taxBenefits={taxBenefits}
            setTaxBenefits={setTaxBenefits}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
